import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Heart, Users, Target, BookOpen, Lightbulb, DollarSign, Shield, BarChart3, Clock, UserCheck, ArrowRight, CheckCircle, AlertTriangle, Globe, Handshake } from "lucide-react";
export default function ManualPage() {
  return <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-yellow-50">
      {/* Hero Section */}
      <section className="hero-gradient text-white py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="mb-8">
            <img src="/images/diverse_children_2.jpeg" alt="Crianças diversas celebrando inclusão" className="w-32 h-32 rounded-full mx-auto mb-6 border-4 border-white/20 shadow-lg" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-[rgb(255,255,255)]">Diretrizes para Promoção de uma Inclusão Real</h1>
          <p className="text-xl md:text-2xl mb-8 text-purple-100 max-w-4xl mx-auto">
            Erradicação do Racismo Inclusivo em Organizações da Sociedade Civil no Brasil
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Badge variant="secondary" className="text-lg px-4 py-2 bg-white/20 text-white border-white/30">
              <Heart className="w-5 h-5 mr-2" />
              Inclusão Real
            </Badge>
            <Badge variant="secondary" className="text-lg px-4 py-2 bg-white/20 text-white border-white/30">
              <Users className="w-5 h-5 mr-2" />
              Antirracista
            </Badge>
            <Badge variant="secondary" className="text-lg px-4 py-2 bg-white/20 text-white border-white/30">
              <Target className="w-5 h-5 mr-2" />
              Interseccional
            </Badge>
          </div>
          <Button size="lg" className="bg-white text-purple-600 hover:bg-purple-50 text-lg px-8 py-3">
            <BookOpen className="w-5 h-5 mr-2" />
            Explorar o Manual
          </Button>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Apresentação */}
        <section className="mb-16">
          <Card className="card-hover border-0 shadow-lg">
            <CardHeader className="text-center pb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-3xl gradient-text mb-4">Apresentação</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 text-lg leading-relaxed">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="mb-4">
                    Este Manual de Diretrizes é um documento fundamental para as Organizações da Sociedade Civil (OSCs) brasileiras que atuam na defesa e promoção dos direitos das pessoas com Síndrome de Down (SD). O objetivo primordial é catalisar uma reorientação estratégica, movendo o ecossistema de apoio de um modelo de inclusão universalista para uma prática explicitamente antirracista e interseccional.
                  </p>
                  <p className="mb-4">
                    O desafio que enfrentamos é a <strong>Disparidade Letal</strong>: a expectativa de vida de indivíduos negros com SD no Brasil pode ser até 50% inferior à de seus pares brancos. Essa disparidade não é biológica, mas uma consequência direta do racismo estrutural e da Dupla Invisibilidade, que limitam o acesso a terapias essenciais desde os primeiros meses de vida.
                  </p>
                  <p className="mb-4">
                    O manual do Instituto InvisibiliDOWN, estruturado a partir da comprovada metodologia de Desenvolvimento Institucional (DI) e do conceito de Competência Estrutural, estabelece que a adesão a uma agenda antirracista não é opcional, mas uma <strong>condição sine qua non</strong> para que as OSCs garantam o direito à saúde, às oportunidades e, fundamentalmente, à vida de todas as pessoas com Síndrome de Down no Brasil.
                  </p>
                </div>
                <div className="text-center">
                  <img src="/images/diverse_children_1.png" alt="Família diversa com inclusão" className="rounded-lg shadow-lg w-full max-w-md mx-auto" />
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-l-4 border-purple-400 p-6 rounded-r-lg">
                <div className="flex items-start">
                  <Globe className="w-6 h-6 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-purple-800 mb-3 text-xl">O Pioneirismo da Proposta: Combatendo a Dupla Invisibilidade</h4>
                    <p className="text-purple-700 mb-4">
                      Esta iniciativa é pioneira porque preenche uma lacuna crítica e globalmente subatendida na intersecção entre raça e deficiência. O Instituto InvisibiliDOWN emergiu como um catalisador fundamental, usando a experiência pessoal de seu fundador, <strong>Thiago Ribeiro</strong>, para expor o <strong>Racismo Inclusivo</strong> — a exclusão racial sutil que opera dentro dos próprios espaços de inclusão.
                    </p>
                    <p className="text-purple-700">
                      O ineditismo dessa abordagem é reforçado pela emergência análoga e quase simultânea de organizações com missões e estratégias semelhantes em diferentes continentes: a <strong>Black Down Syndrome Association (BDSA)</strong> nos EUA e o <strong>Black Child Down Syndrome Project (BCDS)</strong> no Reino Unido. Essa evolução convergente sinaliza um <strong>problema global sistêmico</strong> e valida a urgência de uma agenda explicitamente interseccional, tornando o manual um referencial na vanguarda do advocacy.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Objetivos */}
        <section className="mb-16">
          <Card className="card-hover border-0 shadow-lg">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-500 to-green-500 flex items-center justify-center">
                <Target className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-3xl gradient-text">Objetivos deste Manual</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-6 rounded-lg bg-gradient-to-br from-purple-50 to-blue-50">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-purple-500 flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-3 text-purple-800">Evidenciar a Interseccionalidade</h3>
                  <p className="text-sm text-gray-700">
                    Colocar a raça como o marcador central e mais fatal de desigualdade no contexto da Síndrome de Down.
                  </p>
                </div>
                
                <div className="text-center p-6 rounded-lg bg-gradient-to-br from-blue-50 to-green-50">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-blue-500 flex items-center justify-center">
                    <Handshake className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-3 text-blue-800">Mobilizar Atores</h3>
                  <p className="text-sm text-gray-700">
                    Capacitar e pressionar doadores e a sociedade civil a investirem em Competência Estrutural.
                  </p>
                </div>
                
                <div className="text-center p-6 rounded-lg bg-gradient-to-br from-green-50 to-yellow-50">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-500 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-3 text-green-800">Fortalecer Cultura Antirracista</h3>
                  <p className="text-sm text-gray-700">
                    Promover Justiça de Dados, Busca Ativa e reforma estrutural de programas.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* DIAR - Conceitos */}
        <section className="mb-16">
          <Card className="card-hover border-0 shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl gradient-text mb-4">
                Desenvolvimento Institucional Antirracista (DIAR)
              </CardTitle>
              <p className="text-lg text-gray-600">Conceitos e Urgência</p>
            </CardHeader>
            <CardContent>
              <div className="mb-8 p-6 bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg">
                <p className="text-lg font-medium text-center">
                  O DIAR é a integração dos processos internos de gestão, sustentabilidade e resiliência de uma OSC com um profundo compromisso com a desconstrução do Racismo Institucional.
                </p>
              </div>

              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="racismo-inclusivo" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <div className="flex items-center">
                      <AlertTriangle className="w-5 h-5 mr-3 text-red-500" />
                      <span className="font-semibold">1. Racismo Inclusivo e Disparidade Letal</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4 space-y-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold mb-2 text-red-700">Racismo Inclusivo</h4>
                        <p className="text-sm mb-4">
                          É a forma sutil de exclusão que opera dentro dos próprios espaços dedicados à inclusão. Manifesta-se quando um modelo "tamanho único", implícito como branco e de classe média, falha em reconhecer as barreiras específicas enfrentadas por indivíduos racializados.
                        </p>
                        
                        <h4 className="font-semibold mb-2 text-orange-700">Dupla Invisibilidade</h4>
                        <p className="text-sm">
                          A falha sistêmica em que a pauta de pessoas negras com deficiência é ignorada tanto pelos movimentos de justiça racial quanto pelos movimentos de direitos da pessoa com deficiência.
                        </p>
                      </div>
                      <div className="bg-red-50 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2 text-red-800">Disparidade Letal</h4>
                        <p className="text-sm mb-3">
                          A expectativa de vida para indivíduos negros com SD no Brasil pode ser até 50% inferior à de seus pares brancos.
                        </p>
                        <div className="text-xs space-y-1">
                          <div>🇺🇸 EUA: 50 anos (brancos) vs. 25 anos (negros)</div>
                          <div>🇬🇧 Reino Unido: 62 anos (brancos) vs. 34 anos (minorias)</div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="universalismo" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <div className="flex items-center">
                      <Users className="w-5 h-5 mr-3 text-blue-500" />
                      <span className="font-semibold">2. O Vício do Universalismo</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4 space-y-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <h4 className="font-semibold mb-2 text-blue-800">Efeito Protetor do Capital Socioeconômico</h4>
                          <p className="text-sm">
                            O maior nível educacional dos pais e a mãe ser profissionalmente ativa estão associados a melhor qualidade de vida para pessoas com SD. Famílias negras têm menos probabilidade de possuir esses "fatores protetores".
                          </p>
                        </div>
                        
                        <div className="bg-yellow-50 p-4 rounded-lg">
                          <h4 className="font-semibold mb-2 text-yellow-800">Paradoxo do BPC</h4>
                          <p className="text-sm">
                            O Benefício de Prestação Continuada cria um desincentivo sistêmico ao emprego formal, impactando desproporcionalmente a população negra com deficiência.
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="bg-green-50 p-4 rounded-lg">
                          <h4 className="font-semibold mb-2 text-green-800">Inacessibilidade Velada</h4>
                          <p className="text-sm">
                            Organizações que não consideram custos indiretos (transporte, tempo do cuidador) acabam servindo majoritariamente famílias com capital para superar essas barreiras.
                          </p>
                        </div>
                        
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <h4 className="font-semibold mb-2 text-purple-800">Anulação da Cuidadora</h4>
                          <p className="text-sm">
                            79% dos cuidadores gostariam de trabalhar, mas não podem. A responsabilidade recai majoritariamente sobre mulheres negras e pobres.
                          </p>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="loteria-cep" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <div className="flex items-center">
                      <BarChart3 className="w-5 h-5 mr-3 text-green-500" />
                      <span className="font-semibold">3. Loteria do CEP</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="grid md:grid-cols-2 gap-6 items-center">
                      <div>
                        <p className="mb-4">
                          A Loteria do CEP descreve como o local de residência determina a invisibilidade e o acesso a direitos. A subnotificação é uma manifestação de Racismo Institucional Ativo.
                        </p>
                        
                        <div className="bg-red-50 p-4 rounded-lg mb-4">
                          <h4 className="font-semibold mb-2 text-red-800">Crise da Subnotificação</h4>
                          <p className="text-sm">
                            A prevalência registrada no Brasil (4 por 10.000 NV) está massivamente abaixo das estimativas globais (8 a 14 por 10.000 NV).
                          </p>
                        </div>
                        
                        <div className="bg-orange-50 p-4 rounded-lg">
                          <h4 className="font-semibold mb-2 text-orange-800">Disparidades por Região (2022)</h4>
                          <div className="text-sm space-y-1">
                            <div>📍 Maranhão (NE): 1,0/10.000 NV</div>
                            <div>📍 Rio Grande do Sul (S): 8,0/10.000 NV</div>
                            <div className="font-medium text-orange-700">Variação de 800%!</div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <img src="/images/diverse_children_3.jpeg" alt="Crianças diversas no jardim de infância" className="rounded-lg shadow-lg w-full" />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </section>

        {/* Os 6 Pilares do DIAR */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold gradient-text mb-4">Os 6 Pilares do DIAR</h2>
            <p className="text-xl text-gray-600">Diretrizes e Recomendações para Promoção da Inclusão Real</p>
            <hr className="section-divider w-24 mx-auto mt-6" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Pilar 1 - Intencionalidade */}
            <Card className="card-hover border-0 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                  <Lightbulb className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">DI Precisa de Intencionalidade</CardTitle>
                <p className="text-sm text-gray-600">A Lente Antirracista na Governança</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-purple-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-purple-800 text-sm mb-1">Governança</h4>
                  <p className="text-xs">50%+ representatividade racial em liderança e conselhos</p>
                </div>
                <div className="bg-pink-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-pink-800 text-sm mb-1">Comunicação</h4>
                  <p className="text-xs">60%+ famílias negras/indígenas em material público</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-blue-800 text-sm mb-1">Programas</h4>
                  <p className="text-xs">Abandonar universalismo cego, focar em barreiras compostas</p>
                </div>
              </CardContent>
            </Card>

            {/* Pilar 2 - Dinheiro */}
            <Card className="card-hover border-0 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                  <DollarSign className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">DI Precisa de Dinheiro</CardTitle>
                <p className="text-sm text-gray-600">Financiando a Equidade Estrutural</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-green-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-green-800 text-sm mb-1">Busca Ativa</h4>
                  <p className="text-xs">Recursos para regiões de subnotificação (Norte/Nordeste)</p>
                </div>
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-emerald-800 text-sm mb-1">Investimento DIAR</h4>
                  <p className="text-xs">20% recurso livre para custos operacionais</p>
                </div>
                <div className="bg-teal-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-teal-800 text-sm mb-1">Financiamento</h4>
                  <p className="text-xs">Ciclos plurianuais (mínimo 3 anos consecutivos)</p>
                </div>
              </CardContent>
            </Card>

            {/* Pilar 3 - Confiança */}
            <Card className="card-hover border-0 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">DI Precisa de Confiança</CardTitle>
                <p className="text-sm text-gray-600">Erradicando a Desconfiança Epistêmica</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-blue-800 text-sm mb-1">Modelo LEARN</h4>
                  <p className="text-xs">Listen, Explain, Acknowledge, Recommend, Negotiate</p>
                </div>
                <div className="bg-cyan-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-cyan-800 text-sm mb-1">Competência Estrutural</h4>
                  <p className="text-xs">Rearticular "não adesão" como resposta a barreiras sistêmicas</p>
                </div>
                <div className="bg-indigo-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-indigo-800 text-sm mb-1">Transparência</h4>
                  <p className="text-xs">Espaços de diálogo e devolutivas qualificadas</p>
                </div>
              </CardContent>
            </Card>

            {/* Pilar 4 - Reflexão */}
            <Card className="card-hover border-0 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                  <BarChart3 className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">DI Precisa de Reflexão</CardTitle>
                <p className="text-sm text-gray-600">Justiça de Dados e Vanguarda Global</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-orange-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-orange-800 text-sm mb-1">Justiça de Dados</h4>
                  <p className="text-xs">Coleta obrigatória desagregada por raça, CEP e renda</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-red-800 text-sm mb-1">Indicadores</h4>
                  <p className="text-xs">Auditar Racismo Inclusivo com métricas específicas</p>
                </div>
                <div className="bg-pink-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-pink-800 text-sm mb-1">Colaboração</h4>
                  <p className="text-xs">Parcerias com BDSA, BCDS e grupos interseccionais</p>
                </div>
              </CardContent>
            </Card>

            {/* Pilar 5 - Tempo */}
            <Card className="card-hover border-0 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center">
                  <Clock className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">DI Precisa de Tempo</CardTitle>
                <p className="text-sm text-gray-600">Pensamento de Longo Prazo</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-yellow-800 text-sm mb-1">BPC - Rampas de Saída</h4>
                  <p className="text-xs">Transição suave para trabalho formal</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-orange-800 text-sm mb-1">Moradia Independente</h4>
                  <p className="text-xs">Programa Nacional com financiamento público</p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-amber-800 text-sm mb-1">Dados Oficiais</h4>
                  <p className="text-xs">Pressão por coleta obrigatória no Censo (PL-369/2023)</p>
                </div>
              </CardContent>
            </Card>

            {/* Pilar 6 - Pessoas */}
            <Card className="card-hover border-0 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center">
                  <UserCheck className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">DI Precisa de Pessoas</CardTitle>
                <p className="text-sm text-gray-600">Combate à Anulação da Cuidadora</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-indigo-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-indigo-800 text-sm mb-1">Apoio à Cuidadora</h4>
                  <p className="text-xs">Política Nacional de Apoio aos Cuidadores</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-purple-800 text-sm mb-1">Competência Estrutural</h4>
                  <p className="text-xs">Formação massiva da equipe técnica</p>
                </div>
                <div className="bg-violet-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-violet-800 text-sm mb-1">Protagonismo</h4>
                  <p className="text-xs">"Nada sobre nós, sem nós" - Controle Social SUS/SUAS</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Compromissos Mínimos */}
        <section className="mb-16">
          <Card className="card-hover border-0 shadow-lg bg-gradient-to-br from-red-50 to-orange-50">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-3xl gradient-text">Compromissos Mínimos Inegociáveis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-4 bg-white rounded-lg shadow-sm">
                    <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Justiça de Dados</h4>
                      <p className="text-sm text-gray-600">Coletar e publicar anualmente dados desagregados por raça/cor, CEP e renda</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 bg-white rounded-lg shadow-sm">
                    <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Busca Ativa</h4>
                      <p className="text-sm text-gray-600">Campanhas em regiões de alta subnotificação (Norte/Nordeste)</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 bg-white rounded-lg shadow-sm">
                    <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Representatividade</h4>
                      <p className="text-sm text-gray-600">Meta mínima de 50% de representatividade racial em conselhos e diretorias</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-4 bg-white rounded-lg shadow-sm">
                    <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Competência Estrutural</h4>
                      <p className="text-sm text-gray-600">Treinar 100% da equipe em Comunicação Antirracista (Modelo LEARN)</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 bg-white rounded-lg shadow-sm">
                    <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Reforma de Políticas</h4>
                      <p className="text-sm text-gray-600">Apoiar advocacy pela reforma do BPC e Política Nacional de Cuidadores</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 bg-white rounded-lg shadow-sm">
                    <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Protagonismo</h4>
                      <p className="text-sm text-gray-600">Financiar participação de autodefensores em todas as esferas de decisão</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Conclusão */}
        <section className="mb-16">
          <Card className="card-hover border-0 shadow-lg bg-gradient-to-br from-purple-50 to-blue-50">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl gradient-text mb-4">O Futuro da Inclusão é Antirracista</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-6">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="text-left space-y-4">
                  <p className="text-lg">
                    A luta por uma <strong>Inclusão Real</strong> para pessoas com Síndrome de Down no Brasil exige a erradicação do Racismo Inclusivo.
                  </p>
                  <p>
                    O Desenvolvimento Institucional Antirracista (DIAR) é o alicerce ético que permite às OSCs sustentar a luta contra a Disparidade Letal e garantir o direito inalienável à vida e à dignidade.
                  </p>
                  <p>
                    Ao adotar estas diretrizes, as organizações se posicionam na vanguarda global da advocacia, transformando o universalismo cego em uma prática de justiça social.
                  </p>
                </div>
                <div className="text-center">
                  <img src="/images/inclusion_hands_1.jpeg" alt="Mãos diversas unidas" className="rounded-lg shadow-lg w-full max-w-md mx-auto" />
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-purple-500 to-blue-500 text-white p-6 rounded-lg">
                <p className="text-lg font-medium">
                  Fortalecer o DIAR é o único caminho para transformar a robusta legislação brasileira em uma realidade vivida, desmantelando as barreiras que tornam o CEP um fator de vida ou morte.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Sobre o Instituto */}
        <section className="mb-16">
          <Card className="card-hover border-0 shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl gradient-text mb-4">Sobre o Instituto InvisibiliDOWN</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="mb-4">
                    O Instituto InvisibiliDOWN é uma Organização da Sociedade Civil (OSC) pioneira, dedicada a combater a invisibilidade interseccional e as disparidades sistêmicas que afetam indivíduos negros e indígenas com Síndrome de Down no Brasil e globalmente.
                  </p>
                  <p className="mb-4">
                    Fundado por <strong>Thiago Ribeiro</strong>, pai de Noah, uma criança negra com SD, o Instituto utiliza uma abordagem multifacetada que combina Arte (fotografia documental), Pesquisa rigorosa e Advocacy direcionado.
                  </p>
                  <p>
                    O Instituto cunhou o termo <strong>"Racismo Inclusivo"</strong> e se notabilizou pela "Primeira Pesquisa Nacional sobre Pessoas Negras com Síndrome de Down", influenciando diretamente políticas públicas de saúde.
                  </p>
                </div>
                <div className="text-center">
                  <img src="/images/diverse_children_4.png" alt="Estudantes com síndrome de down" className="rounded-lg shadow-lg w-full" />
                </div>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-3 text-blue-800">Afiliações Estratégicas</h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>• Embaixador do Instituto Jô Clemente</div>
                  <div>• Membro do Conselho Gestor Deliberativo do Observatório de Saúde da População Negra</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Call to Action */}
        <section className="text-center">
          <Card className="card-hover border-0 shadow-lg bg-gradient-to-br from-yellow-50 to-orange-50">
            <CardContent className="py-12">
              <h3 className="text-2xl font-bold mb-4 gradient-text">Transforme sua Organização</h3>
              <p className="text-lg mb-6 text-gray-700">
                Implemente o DIAR e seja parte da mudança para uma inclusão verdadeiramente antirracista
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white">
                  <Heart className="w-5 h-5 mr-2" />
                  Adotar as Diretrizes
                </Button>
                <Button size="lg" variant="outline" className="border-purple-600 text-purple-600 hover:bg-purple-50">
                  <Globe className="w-5 h-5 mr-2" />
                  Saiba Mais
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>;
}